import { SchemaIssue } from "effect"

/**
 * The i18n seam for every validation message in the app.
 *
 * Effect Schema renders a failure into a string at FORMAT time, through two
 * hooks: `leafHook` for structural failures (wrong type, missing key) and
 * `checkHook` for failed refinements. Passing a hook pair to
 * `toStandardSchemaV1` intercepts every message from one place, so schemas stay
 * locale-free and reusable server-side instead of having translations baked into
 * their definitions.
 *
 * This is deliberately ONE seam. Retrofitting locale lookup per-schema later
 * would mean touching every schema in the repo.
 */
export interface FormHooks {
  /** Structural failures: InvalidType, MissingKey, InvalidValue, UnexpectedKey, Forbidden, OneOf. */
  readonly leafHook?: SchemaIssue.LeafHook
  /** Failed refinements (`.check(...)`). Returning `undefined` falls through to the default renderer. */
  readonly checkHook?: SchemaIssue.CheckHook
}

/**
 * The identity hook pair — Effect's own English renderers, named and exported so
 * a locale implementation has something to delegate to for the cases it does not
 * translate:
 *
 * ```ts
 * const leafHook: SchemaIssue.LeafHook = (issue) =>
 *   dict[issue._tag] ?? defaultFormHooks.leafHook(issue)
 *
 * const checkHook: SchemaIssue.CheckHook = (issue) =>
 *   dict[checkId(issue)] ?? defaultFormHooks.checkHook(issue)
 * ```
 *
 * ALWAYS DELEGATE EXPLICITLY; never `return undefined` for the untranslated
 * case. `CheckHook` is typed `=> string | undefined` and undefined does fall
 * through — but it falls through to the `expected`-derived renderer, NOT to
 * `defaultCheckHook`. Since `defaultCheckHook` is what reads a check's own
 * `message` annotation, returning undefined silently replaces every
 * `isMinLength(2, { message: "too short" })` in the app with "Expected a value
 * with a length of at least 2". Delegating preserves them. Pinned by
 * tests/form-validator.test.ts.
 *
 * Both properties are required here (unlike `FormHooks`, where they are optional)
 * so callers can always delegate without a null check.
 */
export const defaultFormHooks: Required<FormHooks> = {
  leafHook: SchemaIssue.defaultLeafHook,
  checkHook: SchemaIssue.defaultCheckHook
}
